import { sanitizeText, normalizeImageUrl } from "./sanitize";
import {
  buildBrand,
  buildSeller,
  buildSizeVariants,
  buildAggregateRating,
  buildReviews,
} from "./builders";
import type {
  StructuredDataProductGroup,
  StructuredDataStandaloneProduct,
  StructuredDataInputProduct,
  StructuredDataRemoteVariant,
  BuildProductGroupSchemaInput,
  BuildProductSchemaInput,
} from "./types";

export type { BuildProductGroupSchemaInput, BuildProductSchemaInput };

const SCHEMA_SIZE = "https://schema.org/size";
const SCHEMA_COLOR = "https://schema.org/color";

function hasLocalColourVariants(product: StructuredDataInputProduct): boolean {
  const colours = new Set(
    product.variants.map((v) => v.colour).filter(Boolean)
  );
  return colours.size > 1;
}

function getVariesBy(
  product: StructuredDataInputProduct,
  facetGroup: BuildProductGroupSchemaInput["facetGroup"]
): string[] {
  const hasColourGroup =
    facetGroup?.facets?.some((f) => f.type === "SupplierColour") ?? false;
  const variesByColor = hasColourGroup || hasLocalColourVariants(product);
  const variesBySize = !product.isNoSize && !product.isOneSize;

  const dimensions: string[] = [];
  if (variesBySize) dimensions.push(SCHEMA_SIZE);
  if (variesByColor) dimensions.push(SCHEMA_COLOR);

  return dimensions.length > 0 ? dimensions : [SCHEMA_SIZE];
}

function getPrimaryVariant(product: StructuredDataInputProduct) {
  const primary = product.variants.find((v) => v.isPrimary);
  return primary ?? product.variants[0];
}

function getPrimaryImageUrl(product: StructuredDataInputProduct): string {
  const img = product.images.find((i) => i.isPrimary) ?? product.images[0];
  return normalizeImageUrl(img?.url);
}

export function buildProductGroupSchema(
  input: BuildProductGroupSchemaInput
): StructuredDataProductGroup {
  const {
    product,
    productDescription,
    canonicalUrl,
    facetGroup,
    stockPrice,
    ratings,
  } = input;

  const primaryVariant = getPrimaryVariant(product);
  if (!primaryVariant) {
    throw new Error(
      "buildProductGroupSchema: product must have at least one variant"
    );
  }

  const productGroupID =
    facetGroup?.facetGroupId != null
      ? String(facetGroup.facetGroupId)
      : String(product.id);

  const variesBy = getVariesBy(product, facetGroup);
  const variesByColor = variesBy.includes(SCHEMA_COLOR);

  const entity: StructuredDataProductGroup = {
    "@type": "ProductGroup",
    name: sanitizeText(product.name),
    productGroupID,
    variesBy,
    sku: sanitizeText(primaryVariant.sku),
    image: getPrimaryImageUrl(product),
    brand: buildBrand(product.brandName),
    url: canonicalUrl,
  };

  if (!variesByColor) {
    entity.color = sanitizeText(primaryVariant.colour);
  }

  const sanitizedDescription = sanitizeText(productDescription);
  if (sanitizedDescription) {
    entity.description = sanitizedDescription;
  }

  const hasStockPrice = Array.isArray(stockPrice) && stockPrice.length > 0;
  const isDiscontinued = product.isDiscontinued === true;
  const remoteVariants: StructuredDataRemoteVariant[] = (
    facetGroup?.colourVariantUrls ?? []
  )
    .map((url) => normalizeImageUrl(url))
    .filter(Boolean)
    .map((url) => ({ url }));

  if (!isDiscontinued) {
    const localVariants = hasStockPrice
      ? buildSizeVariants(product, stockPrice)
      : [];
    const allVariants = [...localVariants, ...remoteVariants];
    if (allVariants.length > 0) {
      entity.hasVariant = allVariants;
    }
  }

  if (hasStockPrice && ratings) {
    const aggregateRating = buildAggregateRating(ratings);
    if (aggregateRating) {
      entity.aggregateRating = aggregateRating;
    }

    const reviews = buildReviews(ratings.reviews ?? []);
    if (reviews.length > 0) {
      entity.review = reviews;
    }
  }

  return entity;
}

function shouldUseProductGroup(input: BuildProductSchemaInput): boolean {
  const { product, facetGroup } = input;

  if (product.variants.length > 1) return true;

  const hasColourGroup =
    facetGroup?.facets?.some((f) => f.type === "SupplierColour") ?? false;
  if (hasColourGroup) return true;

  const hasRemoteVariants =
    (facetGroup?.colourVariantUrls ?? []).filter(Boolean).length > 0;
  if (hasRemoteVariants) return true;

  return false;
}

export function buildStandaloneProductSchema(
  input: BuildProductSchemaInput
): StructuredDataStandaloneProduct {
  const { product, productDescription, canonicalUrl, stockPrice, ratings } =
    input;

  const primaryVariant = getPrimaryVariant(product);
  if (!primaryVariant) {
    throw new Error(
      "buildStandaloneProductSchema: product must have at least one variant"
    );
  }

  const entity: StructuredDataStandaloneProduct = {
    "@type": "Product",
    name: sanitizeText(product.name),
    sku: sanitizeText(primaryVariant.sku),
    color: sanitizeText(primaryVariant.colour),
    image: getPrimaryImageUrl(product),
    brand: buildBrand(product.brandName),
    url: canonicalUrl,
  };

  if (primaryVariant.ean) {
    entity.gtin13 = sanitizeText(primaryVariant.ean);
  }

  const sanitizedDescription = sanitizeText(productDescription);
  if (sanitizedDescription) {
    entity.description = sanitizedDescription;
  }

  const hasStockPrice = Array.isArray(stockPrice) && stockPrice.length > 0;
  const isDiscontinued = product.isDiscontinued === true;

  if (hasStockPrice && !isDiscontinued) {
    const stockEntry = stockPrice.find(
      (sp) => String(sp.id) === String(primaryVariant.variantId)
    );
    if (stockEntry) {
      entity.offers = {
        "@type": "Offer",
        price: stockEntry.price.current.value,
        priceCurrency: sanitizeText(stockEntry.price.currency),
        availability: stockEntry.isInStock
          ? "https://schema.org/InStock"
          : "https://schema.org/OutOfStock",
        itemCondition: "https://schema.org/NewCondition",
        seller: buildSeller(),
      };
    }
  }

  if (hasStockPrice && ratings) {
    const aggregateRating = buildAggregateRating(ratings);
    if (aggregateRating) {
      entity.aggregateRating = aggregateRating;
    }

    const reviews = buildReviews(ratings.reviews ?? []);
    if (reviews.length > 0) {
      entity.review = reviews;
    }
  }

  return entity;
}

export function buildProductSchema(
  input: BuildProductSchemaInput
): StructuredDataProductGroup | StructuredDataStandaloneProduct {
  if (shouldUseProductGroup(input)) {
    return buildProductGroupSchema(input);
  }
  return buildStandaloneProductSchema(input);
}
